<template>
    <div class="ds-settings-stack">
        <SettingsCard id="block-availability" title="Доступность вакансии" class="ds-settings-block">
            <template #actions>
                <span class="ds-settings-muted">Кто и когда видит вакансию</span>
            </template>

            <SettingsBlockState
                :status="editor.load.global"
                :rows="2"
                error-text="Не удалось загрузить настройки биржи смен."
                @retry="editor.loadGlobal()"
            >
                <!-- Два текущих значения в минутах; точка отсчёта задержек схемами не установлена — не подписываем. -->
                <div class="ds-settings-stages">
                    <div class="ds-settings-stage-line" aria-hidden="true">
                        <span v-for="index in 3" :key="index" class="ds-settings-stage-step">
                            <span class="ds-settings-stage-dot"></span>
                            <span v-if="index < 3" class="ds-settings-stage-track"></span>
                        </span>
                    </div>
                    <div class="ds-settings-stage-cards">
                        <div class="ds-settings-stage">
                            <div class="ds-settings-muted">Сразу</div>
                            <div class="ds-settings-stage-title">Свой магазин</div>
                            <div class="ds-settings-stage-text">Сотрудники магазина, где открыта вакансия</div>
                        </div>
                        <div
                            v-for="field in EXCHANGE_DELAY_FIELDS"
                            :id="`setting-${field.key}`"
                            :key="field.key"
                            class="ds-settings-stage"
                            :class="{
                                'is-changed': editor.changedAnchors.value.has(`setting-${field.key}`),
                                'is-highlighted': editor.highlightedAnchor.value === `setting-${field.key}`,
                            }"
                        >
                            <div class="ds-settings-muted">Через</div>
                            <SettingsNumberField
                                :model-value="numberValue(field.key)"
                                :label="`${field.label}: через, минут`"
                                :unit="field.unit"
                                :changed="editor.changedAnchors.value.has(`setting-${field.key}`)"
                                :invalid="parseNonNegativeInt(numberValue(field.key)) === null"
                                error-text="Введите целое число не меньше 0"
                                :disabled="editor.saving.value"
                                @update:model-value="editor.setGlobal(field.key, $event)"
                            />
                            <div class="ds-settings-stage-title">{{ field.label }}</div>
                            <div class="ds-settings-stage-text">{{ stageText[field.key] }}</div>
                        </div>
                    </div>
                </div>
            </SettingsBlockState>
        </SettingsCard>

        <SettingsCard id="block-rules" title="Правила подбора" class="ds-settings-block">
            <template v-if="editor.globalReady.value" #actions>
                <span class="ds-settings-muted">{{ enabledRules }} из {{ EXCHANGE_RULE_FIELDS.length }}</span>
            </template>

            <SettingsBlockState
                :status="editor.load.global"
                :rows="5"
                error-text="Не удалось загрузить правила подбора."
                @retry="editor.loadGlobal()"
            >
                <div class="ds-settings-rules-table" role="table" aria-label="Правила подбора">
                    <div class="ds-settings-rule-row ds-settings-param-head" role="row">
                        <span role="columnheader">Правило</span>
                        <span role="columnheader">Вкл.</span>
                        <span role="columnheader">Как работает</span>
                    </div>
                    <div
                        v-for="field in EXCHANGE_RULE_FIELDS"
                        :id="`setting-${field.key}`"
                        :key="field.key"
                        class="ds-settings-rule-row"
                        :class="{
                            'is-changed': editor.changedAnchors.value.has(`setting-${field.key}`),
                            'is-highlighted': editor.highlightedAnchor.value === `setting-${field.key}`,
                        }"
                        role="row"
                    >
                        <span class="ds-settings-param-name" role="cell">{{ field.label }}</span>
                        <span role="cell">
                            <BooleanSwitch
                                :model-value="booleanValue(field.key)"
                                :disabled="editor.saving.value"
                                @update:model-value="editor.setGlobal(field.key, $event)"
                            ><span class="sr-only">{{ field.label }}</span></BooleanSwitch>
                        </span>
                        <span class="ds-settings-param-hint" role="cell">{{ field.description }}</span>
                    </div>
                </div>
            </SettingsBlockState>
        </SettingsCard>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SettingsCard from '@/ui/SettingsCard.vue'
import BooleanSwitch from '@/ui/BooleanSwitch.vue'
import SettingsBlockState from './SettingsBlockState.vue'
import SettingsNumberField from './SettingsNumberField.vue'
import { useSettingsEditor } from './useSettingsEditor'
import {
    EXCHANGE_DELAY_FIELDS,
    EXCHANGE_RULE_FIELDS,
    parseNonNegativeInt,
    type GlobalBooleanKey,
    type GlobalNumberKey,
} from './settingsModel'

const editor = useSettingsEditor()

const stageText: Partial<Record<GlobalNumberKey, string>> = {
    MINUTES_UNTIL_AVAILABLE_INNER_WORKERS: 'Сотрудники других магазинов сети',
    MINUTES_UNTIL_AVAILABLE_OUTER_WORKERS: 'Сотрудники аутсорс-партнёров',
}

const numberValue = (key: GlobalNumberKey) => {
    const value = editor.globalValue(key)
    return typeof value === 'string' ? value : null
}

const booleanValue = (key: GlobalBooleanKey) => editor.globalValue(key) === true

const enabledRules = computed(() => EXCHANGE_RULE_FIELDS.filter((field) => booleanValue(field.key)).length)
</script>
